import { useMemo } from 'react';

import type { AudienceOptionType } from '@/api/audience';
import type { ActivityDataType, NumericKeys } from '@/api/common';
import { TRAFFIC_TYPES } from '@/api/common';

type VisitsMetricFields = {
  target: NumericKeys<ActivityDataType>;
  other: NumericKeys<ActivityDataType>;
  bot: NumericKeys<ActivityDataType>;
  unresolved: NumericKeys<ActivityDataType>;
};

const DEFAULT_VISITS_METRIC_FIELDS = {
  target: 'targetVisits',
  other: 'otherVisits',
  bot: 'botVisits',
  unresolved: 'unresolvedVisits',
} satisfies VisitsMetricFields;

/**
 * Calculates the max visits stats for a given data set. Takes disabled series into account and
 * whether the audience is a TAM or not.
 *
 * @param data - The data to calculate the max visits stats from.
 * @param audienceId - The audience ID to calculate the max visits stats for.
 * @param disabledSeries - The series to disable in the max visits stats.
 * @returns The max visits stats.
 */
const useMaxVisitsStats = ({
  data,
  audience,
  disabledSeries,
  metricFields = DEFAULT_VISITS_METRIC_FIELDS,
  sumWithBaseMetrics = false,
}: {
  data: ActivityDataType[] | undefined;
  audience?: AudienceOptionType;
  disabledSeries?: TRAFFIC_TYPES[];
  metricFields?: VisitsMetricFields;
  sumWithBaseMetrics?: boolean;
}) => {
  return useMemo(
    () =>
      data?.reduce((max, d) => {
        const getSeriesValue = (
          metricField: NumericKeys<ActivityDataType>,
          baseMetricField: NumericKeys<ActivityDataType>,
        ) => (d[metricField] ?? 0) + (sumWithBaseMetrics ? (d[baseMetricField] ?? 0) : 0);

        let total = 0;

        if (audience?.isTAM) {
          if (!disabledSeries?.includes(TRAFFIC_TYPES.TAM)) {
            total += getSeriesValue(metricFields.target, DEFAULT_VISITS_METRIC_FIELDS.target);
          }
        } else {
          if (!disabledSeries?.includes(TRAFFIC_TYPES.AUDIENCE)) {
            total += getSeriesValue(metricFields.target, DEFAULT_VISITS_METRIC_FIELDS.target);
          }
        }
        if (!disabledSeries?.includes(TRAFFIC_TYPES.OTHER)) {
          total += getSeriesValue(metricFields.other, DEFAULT_VISITS_METRIC_FIELDS.other);
        }
        if (!disabledSeries?.includes(TRAFFIC_TYPES.BOT)) {
          total += getSeriesValue(metricFields.bot, DEFAULT_VISITS_METRIC_FIELDS.bot);
        }
        if (!disabledSeries?.includes(TRAFFIC_TYPES.UNRESOLVED)) {
          total += getSeriesValue(metricFields.unresolved, DEFAULT_VISITS_METRIC_FIELDS.unresolved);
        }

        return Math.max(total, max);
      }, 0),
    [
      data,
      audience?.isTAM,
      disabledSeries,
      metricFields.target,
      metricFields.other,
      metricFields.bot,
      metricFields.unresolved,
      sumWithBaseMetrics,
    ],
  );
};

export default useMaxVisitsStats;
